import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, Mail, Send, Trash2, X } from "lucide-react";
import Modal from "components/common/Modal";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import ConfirmDialog from "../common/ConfirmDialog";
import { ConsentStatusBadge, formatDate } from "./consentShared";

const PURPOSES = ["club_tracking", "research"];
// The parent's private link works for 12 months after it's sent (API).
const LINK_LIFETIME_MS = 365 * 24 * 60 * 60 * 1000;

const downloadJson = (data, filename) => {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * One player's consent: their parent, current choices, the full history
 * (sign-up, each choice, approval), and for workspace admins, exporting or
 * deleting the player's data.
 */
const PlayerConsentModal = ({
  show,
  onClose,
  workspaceId,
  teamId,
  playerId,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [player, setPlayer] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  // The parent passes an inline onClose; keep it out of the load effect.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!show || !playerId) return;
    setPlayer(null);
    setConfirmingDelete(false);
    setConfirmName("");
    betaAPI
      .getPlayerConsent({ workspaceId, teamId, playerId })
      .then(({ data }) => setPlayer(data))
      .catch((err) => {
        console.error("Failed to load player consent", err);
        showToast.error(t("team.consentTab.toasts.loadFailed"));
        close.current();
      });
  }, [show, playerId, workspaceId, teamId, t]);

  const [confirmResend, setConfirmResend] = useState(false);
  const [resending, setResending] = useState(false);
  const handleResend = async () => {
    setConfirmResend(false);
    setResending(true);
    try {
      const { data } = await betaAPI.resendConsentLink({
        workspaceId,
        teamId,
        playerId,
      });
      setPlayer((prev) => ({
        ...prev,
        consent_link_sent_at: data.sent_at,
        consent_link_revoked_at: null,
        consent_link_expires_at: new Date(
          new Date(data.sent_at).getTime() + LINK_LIFETIME_MS
        ).toISOString(),
      }));
      showToast.success(
        t("team.consentTab.toasts.linkSent", { email: data.sent_to })
      );
    } catch (err) {
      console.error("Failed to resend consent link", err);
      showToast.error(
        err.payload?.code === "too_soon"
          ? t("team.consentTab.toasts.linkTooSoon", {
              count: Math.ceil((err.payload.retry_after_seconds || 60) / 60),
            })
          : t("team.consentTab.toasts.linkFailed")
      );
    } finally {
      setResending(false);
    }
  };

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const handleRevoke = async () => {
    setConfirmRevoke(false);
    try {
      const { data } = await betaAPI.revokeConsentLink({
        workspaceId,
        teamId,
        playerId,
      });
      setPlayer((prev) => ({
        ...prev,
        consent_link_revoked_at: data.revoked_at,
        consent_link_expires_at: null,
      }));
      showToast.success(t("team.consentTab.toasts.linkRevoked"));
    } catch (err) {
      console.error("Failed to revoke consent link", err);
      showToast.error(t("team.consentTab.toasts.linkRevokeFailed"));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await betaAPI.exportPlayerData({
        workspaceId,
        teamId,
        playerId,
      });
      const slug = player.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      downloadJson(
        data,
        `${slug}-data-${new Date().toISOString().slice(0, 10)}.json`
      );
    } catch (err) {
      console.error("Failed to export player data", err);
      showToast.error(t("team.consentTab.toasts.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await betaAPI.deletePlayerData({
        workspaceId,
        teamId,
        playerId,
        confirmName,
      });
      showToast.success(
        t("team.consentTab.toasts.deleted", { name: player.name })
      );
      onDeleted?.();
    } catch (err) {
      console.error("Failed to delete player data", err);
      showToast.error(
        err.payload?.code === "legal_hold"
          ? t("team.consentTab.toasts.legalHold")
          : t("team.consentTab.toasts.deleteFailed")
      );
    } finally {
      setDeleting(false);
    }
  };

  const eventLabel = (e) => {
    if (e.kind === "signed_up")
      return t("team.consentTab.history.signedUp", { name: e.by });
    if (e.kind === "approved")
      return t("team.consentTab.history.approved", { name: e.by });
    if (e.purpose === "guardian_declaration")
      return t("team.consentTab.history.declared");
    return t(
      e.granted
        ? "team.consentTab.history.granted"
        : "team.consentTab.history.declined",
      { purpose: t(`team.consentTab.purposes.${e.purpose}`) }
    );
  };

  return (
    <Modal show={show} onClose={onClose} maxWidth="640px">
      {!player ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-[#E07A3A]" />
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 border-b border-[#F3F4F6] px-7 pb-5 pt-6">
            <div>
              <h2 className="text-lg font-bold text-[#111827]">
                {player.name}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-[#6B7280]">
                <ConsentStatusBadge player={player} />
                {player.date_of_birth && (
                  <span>
                    {t("team.consentTab.born", {
                      date: formatDate(player.date_of_birth),
                    })}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={t("common.actions.close")}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-[#6B7280] hover:bg-[#F3F4F6]"
            >
              <X size={18} />
            </button>
          </div>

          <div className="max-h-[65vh] space-y-6 overflow-y-auto px-7 py-6">
            {/* Parent */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                {t("team.consentTab.detail.parent")}
              </h3>
              {player.guardian ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[#F1F2F4] px-4 py-3">
                  <div>
                    <p className="font-medium text-[#111827]">
                      {player.guardian.name}
                    </p>
                    <p className="text-sm text-[#6B7280]">
                      {player.guardian.email}
                    </p>
                    {player.consent_link_revoked_at ? (
                      <p className="mt-0.5 text-xs text-[#DC2626]">
                        {t("team.consentTab.detail.linkRevokedOn", {
                          date: formatDate(player.consent_link_revoked_at, {
                            day: "numeric",
                            month: "short",
                          }),
                        })}
                      </p>
                    ) : (
                      player.consent_link_sent_at && (
                        <p className="mt-0.5 text-xs text-[#9CA3AF]">
                          {t("team.consentTab.detail.linkLastSent", {
                            date: formatDate(player.consent_link_sent_at, {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            }),
                          })}
                          {player.consent_link_expires_at && (
                            <>
                              {" · "}
                              {t("team.consentTab.detail.linkWorksUntil", {
                                date: formatDate(
                                  player.consent_link_expires_at,
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  }
                                ),
                              })}
                            </>
                          )}
                        </p>
                      )
                    )}
                    {!player.consent_link_revoked_at && (
                      <button
                        onClick={() => setConfirmRevoke(true)}
                        className="mt-1 text-xs font-medium text-[#6B7280] underline underline-offset-2 hover:text-[#DC2626]"
                      >
                        {t("team.consentTab.detail.revokeLink")}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      onClick={() => setConfirmResend(true)}
                      disabled={resending}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-60"
                    >
                      {resending ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Send size={15} className="text-[#E07A3A]" />
                      )}
                      {t("team.consentTab.detail.resendLink")}
                    </button>
                    <a
                      href={`mailto:${player.guardian.email}`}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]"
                      aria-label={t("team.consentTab.detail.email", {
                        name: player.guardian.name,
                      })}
                    >
                      <Mail size={16} />
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#6B7280]">
                  {t("team.consentTab.detail.noParent")}
                </p>
              )}
            </section>

            {/* Current choices */}
            {player.consents && (
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                  {t("team.consentTab.detail.choices")}
                </h3>
                <ul className="divide-y divide-[#F3F4F6] rounded-lg border border-[#F1F2F4]">
                  {(player.purposes || PURPOSES).map((p) => (
                    <li
                      key={p}
                      className="flex items-center justify-between px-4 py-2.5 text-sm"
                    >
                      <span className="text-[#374151]">
                        {t(`team.consentTab.choiceLabels.${p}`)}
                      </span>
                      <span
                        className={`font-semibold ${
                          player.consents[p]
                            ? "text-[#047857]"
                            : "text-[#6B7280]"
                        }`}
                      >
                        {player.consents[p]
                          ? t("team.consentTab.yes")
                          : t("team.consentTab.no")}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* History */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                {t("team.consentTab.detail.history")}
              </h3>
              {player.history.length === 0 ? (
                <p className="text-sm text-[#6B7280]">
                  {t("team.consentTab.detail.noHistory")}
                </p>
              ) : (
                <ol className="relative space-y-3 border-l border-[#E5E7EB] pl-5">
                  {player.history.map((e, i) => (
                    <li key={i} className="relative">
                      <span
                        className={`absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white ${
                          e.kind === "consent" && !e.granted
                            ? "bg-[#9CA3AF]"
                            : "bg-[#E07A3A]"
                        }`}
                      />
                      <p className="text-sm text-[#111827]">{eventLabel(e)}</p>
                      <p className="text-xs text-[#6B7280]">
                        {formatDate(e.at, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {e.kind === "consent" &&
                          ` · ${t("team.consentTab.history.by", {
                            name: e.by,
                          })} · ${t("team.consentTab.history.wording", {
                            version: e.wording_version,
                          })}`}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
              {player.deletion_scheduled_for && (
                <p className="mt-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
                  {t("team.consentTab.detail.deletionScheduled", {
                    date: formatDate(player.deletion_scheduled_for),
                  })}
                </p>
              )}
            </section>

            {/* Data requests */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                {t("team.consentTab.detail.requests")}
              </h3>
              {!player.can_manage ? (
                <p className="text-sm text-[#6B7280]">
                  {t("team.consentTab.detail.adminsOnly")}
                </p>
              ) : confirmingDelete ? (
                <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4">
                  <p className="text-sm text-[#7F1D1D]">
                    {t("team.consentTab.detail.deleteWarning", {
                      name: player.name,
                    })}
                  </p>
                  <input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={player.name}
                    aria-label={t("team.consentTab.detail.typeName")}
                    className="mt-3 w-full rounded-lg border border-[#FECACA] bg-white px-3 py-2 text-sm outline-none focus:border-[#DC2626]"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={() => setConfirmingDelete(false)}
                      className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151]"
                    >
                      {t("common.actions.cancel")}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={
                        deleting ||
                        confirmName.trim().toLowerCase() !==
                          player.name.trim().toLowerCase()
                      }
                      className="rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t("team.consentTab.detail.deleteConfirm")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExport}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-60"
                  >
                    {exporting ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Download size={15} />
                    )}
                    {t("team.consentTab.detail.export")}
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(true)}
                    disabled={player.legal_hold}
                    title={
                      player.legal_hold
                        ? t("team.consentTab.toasts.legalHold")
                        : undefined
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-[#FECACA] bg-white px-4 py-2 text-sm font-medium text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-50"
                  >
                    <Trash2 size={15} />
                    {t("team.consentTab.detail.delete")}
                  </button>
                </div>
              )}
            </section>
          </div>
        </>
      )}
      <ConfirmDialog
        open={confirmResend}
        title={t("team.consentTab.detail.resendTitle", {
          name: player?.guardian?.name ?? "",
        })}
        body={t("team.consentTab.detail.resendBody", {
          email: player?.guardian?.email ?? "",
        })}
        confirmLabel={t("team.consentTab.detail.resendConfirm")}
        onConfirm={handleResend}
        onCancel={() => setConfirmResend(false)}
      />
      <ConfirmDialog
        open={confirmRevoke}
        title={t("team.consentTab.detail.revokeTitle")}
        body={t("team.consentTab.detail.revokeBody", {
          name: player?.guardian?.name ?? "",
        })}
        confirmLabel={t("team.consentTab.detail.revokeConfirm")}
        danger
        onConfirm={handleRevoke}
        onCancel={() => setConfirmRevoke(false)}
      />
    </Modal>
  );
};

export default PlayerConsentModal;
